/**
 * Generador de Invitaciones - Panel Admin
 * 
 * Permite crear códigos de invitación únicos con:
 * - Tier (standard, vip, ultra_vip)
 * - Descuento personalizado
 * - Límite de usos
 * - Fecha de expiración
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { INVITATION_DEFAULT_LAYOUT } from "@/config/invitationDefaultLayout";
import { BRAND_SITE_URL } from "@/lib/brandConfig";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import InviteExpiryBadge from "@/components/admin/InviteExpiryBadge";
import InviteDmTemplate from "@/components/admin/InviteDmTemplate";
import { useState, useEffect, useCallback } from "react";
import {
  Copy,
  Check,
  Link as LinkIcon,
  Sparkles,
  Crown,
  Gift,
  Plus,
  Link2,
  Infinity as InfinityIcon,
  Loader2,
  Trash2,
  RefreshCw,
} from "lucide-react";

interface GeneratedCode {
  id?: string;
  code: string;
  tier: "standard" | "vip" | "ultra_vip";
  maxUses: number;
  currentUses?: number;
  expiresAt: Date;
  link: string;
  status?: "active" | "expired" | "exhausted" | "inactive";
}


export default function GeneradorInvitaciones() {
  const { getAuthHeaders } = useAdminAuth();
  const [codes, setCodes] = useState<GeneratedCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesError, setCodesError] = useState<string | null>(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Link de un solo uso (compartir y abrir 1 vez)
  const [oneTimeLinkUrl, setOneTimeLinkUrl] = useState<string | null>(null);
  const [oneTimeLinkPassword, setOneTimeLinkPassword] = useState("");
  const [oneTimeLinkLoading, setOneTimeLinkLoading] = useState(false);
  const [oneTimeLinkError, setOneTimeLinkError] = useState<string | null>(null);
  const [oneTimeLinkCopied, setOneTimeLinkCopied] = useState(false);
  const [adminLinkCopied, setAdminLinkCopied] = useState(false);

  // Link de invitación (tiempo configurable + layout para página destino)
  const [invitation1MinUrl, setInvitation1MinUrl] = useState<string | null>(null);
  const [invitation1MinExpiresAt, setInvitation1MinExpiresAt] = useState<string | null>(null);
  const [invitation1MinPassword, setInvitation1MinPassword] = useState("");
  const [invitation1MinLoading, setInvitation1MinLoading] = useState(false);
  const [invitation1MinError, setInvitation1MinError] = useState<string | null>(null);
  const [invitation1MinCopied, setInvitation1MinCopied] = useState(false);
  const [invitationValiditySeconds, setInvitationValiditySeconds] = useState(1800);
  const [invitationLayout, setInvitationLayout] = useState(() => ({
    ...INVITATION_DEFAULT_LAYOUT,
  }));

  const adminInvitacionLink = `${typeof window !== "undefined" ? window.location.origin : ""}/acceso`;

  const copyAdminLink = async () => {
    try {
      await navigator.clipboard.writeText(adminInvitacionLink);
      setAdminLinkCopied(true);
      setTimeout(() => setAdminLinkCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };
  
  // Form state
  const [tier, setTier] = useState<"standard" | "vip" | "ultra_vip">("standard");
  const [maxUses, setMaxUses] = useState(50);
  const [expirationDays, setExpirationDays] = useState(30);
  const [prefix, setPrefix] = useState("");

  // ── Load permanent codes from DB ─────────────────────────────────────────
  const loadCodes = useCallback(async () => {
    setCodesLoading(true);
    setCodesError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/invitation-codes", { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCodesError(data.error || "Error cargando códigos");
        return;
      }
      const mapped: GeneratedCode[] = (data.codes || []).map((c: Record<string, unknown>) => ({
        id: c.id as string,
        code: c.code as string,
        tier: (c.tier as GeneratedCode["tier"]) || "standard",
        maxUses: Number(c.max_uses ?? 50),
        currentUses: Number(c.current_uses ?? 0),
        expiresAt: new Date(c.expires_at as string),
        link: c.link as string,
        status: (c.status as GeneratedCode["status"]) || "active",
      }));
      setCodes(mapped);
    } catch {
      setCodesError("Error de conexión");
    } finally {
      setCodesLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  // ── Create permanent code via API ─────────────────────────────────────────
  const handleGenerateCode = async () => {
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/invitation-codes", {
        method: "POST",
        headers,
        body: JSON.stringify({
          tier,
          max_uses: maxUses,
          expiration_days: expirationDays,
          prefix: prefix || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setGenerateError(data.error || "Error al generar código");
        return;
      }
      const newCode: GeneratedCode = {
        id: data.id as string,
        code: data.code as string,
        tier: (data.tier as GeneratedCode["tier"]) || "standard",
        maxUses: Number(data.max_uses ?? 50),
        currentUses: 0,
        expiresAt: new Date(data.expires_at as string),
        link: data.link as string,
        status: "active",
      };
      setCodes((prev) => [newCode, ...prev]);
    } catch {
      setGenerateError("Error de conexión");
    } finally {
      setGenerateLoading(false);
    }
  };

  // ── Deactivate code ────────────────────────────────────────────────────────
  const handleDeactivate = async (id: string) => {
    if (!id) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/invitation-codes?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers,
      });
      if (res.ok) {
        setCodes((prev) =>
          prev.map((c) => c.id === id ? { ...c, status: "inactive" as const } : c)
        );
      }
    } catch { /* silently ignore */ }
  };
  
  const handleCopyLink = async (code: GeneratedCode) => {
    try {
      await navigator.clipboard.writeText(code.link);
      setCopiedCode(code.code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Error copying:", err);
    }
  };
  
  // ── AR direct VIP invitation (expires fast, lands on /exclusive-ar) ──────
  // Shareable link → MuxVideoPlayerWithCTA → MercadoPago VIP checkout (ARS).
  // Tracked in SECONDS so the operator can mint burst links ("60 segundos
  // para decidir") for high-pressure DM campaigns.
  const [arInviteSeconds, setArInviteSeconds] = useState(120); // default: 2 min
  const [arInviteMaxUses, setArInviteMaxUses] = useState(1);
  const [arInviteUrl, setArInviteUrl] = useState<string | null>(null);
  const [arInviteCode, setArInviteCode] = useState<string | null>(null);
  const [arInviteExpiresAt, setArInviteExpiresAt] = useState<string | null>(null);
  const [arInviteLoading, setArInviteLoading] = useState(false);
  const [arInviteError, setArInviteError] = useState<string | null>(null);
  const [arInviteCopied, setArInviteCopied] = useState(false);

  const handleGenerateArDirectInvite = async () => {
    setArInviteLoading(true);
    setArInviteError(null);
    setArInviteUrl(null);
    setArInviteCode(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/invitation-codes", {
        method: "POST",
        headers,
        body: JSON.stringify({
          tier: "vip",
          max_uses: arInviteMaxUses,
          expiration_seconds: arInviteSeconds,
          prefix: "AR",
          destination: "exclusive_ar",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setArInviteError(data.error || "Error al generar la invitación");
        return;
      }
      setArInviteUrl(data.link as string);
      setArInviteCode(data.code as string);
      setArInviteExpiresAt(data.expires_at as string);
      // Also append to the codes list so it shows up in the table
      setCodes((prev) => [
        {
          id: data.id as string,
          code: data.code as string,
          tier: "vip",
          maxUses: Number(data.max_uses ?? 1),
          currentUses: 0,
          expiresAt: new Date(data.expires_at as string),
          link: data.link as string,
          status: "active",
        },
        ...prev,
      ]);
    } catch {
      setArInviteError("Error de conexión");
    } finally {
      setArInviteLoading(false);
    }
  };

  const handleCopyArInvite = async () => {
    if (!arInviteUrl) return;
    try {
      await navigator.clipboard.writeText(arInviteUrl);
      setArInviteCopied(true);
      setTimeout(() => setArInviteCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  // ── International direct invitation (expires fast, lands on /exclusive) ──
  // Invitacion Miembro: DualCTA video landing (/invitacion-miembro?code=CODE).
  // AR → MercadoPago VIP direct, non-AR → Stripe embedded overlay.
  const [miembroInviteSeconds, setMiembroInviteSeconds] = useState(120);
  const [miembroInviteMaxUses, setMiembroInviteMaxUses] = useState(1);
  const [miembroInviteUrl, setMiembroInviteUrl] = useState<string | null>(null);
  const [miembroInviteCode, setMiembroInviteCode] = useState<string | null>(null);
  const [miembroInviteExpiresAt, setMiembroInviteExpiresAt] = useState<string | null>(null);
  const [miembroInviteLoading, setMiembroInviteLoading] = useState(false);
  const [miembroInviteError, setMiembroInviteError] = useState<string | null>(null);
  const [miembroInviteCopied, setMiembroInviteCopied] = useState(false);

  // Same pattern as the AR variant but international: Stripe USD checkout,
  // overridePackId="exclusive". Useful for sending burst links via DM to
  // non-AR followers (US/MX/ES/etc).
  const [intlInviteSeconds, setIntlInviteSeconds] = useState(120);
  const [intlInviteMaxUses, setIntlInviteMaxUses] = useState(1);
  const [intlInviteUrl, setIntlInviteUrl] = useState<string | null>(null);
  const [intlInviteCode, setIntlInviteCode] = useState<string | null>(null);
  const [intlInviteExpiresAt, setIntlInviteExpiresAt] = useState<string | null>(null);
  const [intlInviteLoading, setIntlInviteLoading] = useState(false);
  const [intlInviteError, setIntlInviteError] = useState<string | null>(null);
  const [intlInviteCopied, setIntlInviteCopied] = useState(false);

  const handleGenerateIntlDirectInvite = async () => {
    setIntlInviteLoading(true);
    setIntlInviteError(null);
    setIntlInviteUrl(null);
    setIntlInviteCode(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/invitation-codes", {
        method: "POST",
        headers,
        body: JSON.stringify({
          tier: "vip",
          max_uses: intlInviteMaxUses,
          expiration_seconds: intlInviteSeconds,
          prefix: "INTL",
          destination: "exclusive",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setIntlInviteError(data.error || "Error al generar la invitación");
        return;
      }
      setIntlInviteUrl(data.link as string);
      setIntlInviteCode(data.code as string);
      setIntlInviteExpiresAt(data.expires_at as string);
      setCodes((prev) => [
        {
          id: data.id as string,
          code: data.code as string,
          tier: "vip",
          maxUses: Number(data.max_uses ?? 1),
          currentUses: 0,
          expiresAt: new Date(data.expires_at as string),
          link: data.link as string,
          status: "active",
        },
        ...prev,
      ]);
    } catch {
      setIntlInviteError("Error de conexión");
    } finally {
      setIntlInviteLoading(false);
    }
  };

  const handleGenerateMiembroInvite = async () => {
    setMiembroInviteLoading(true);
    setMiembroInviteError(null);
    setMiembroInviteUrl(null);
    setMiembroInviteCode(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/invitation-codes", {
        method: "POST",
        headers,
        body: JSON.stringify({
          tier: "vip",
          max_uses: miembroInviteMaxUses,
          expiration_seconds: miembroInviteSeconds,
          prefix: "MBR",
          destination: "invitacion_miembro",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMiembroInviteError(data.error || "Error al generar la invitación");
        return;
      }
      setMiembroInviteUrl(data.link as string);
      setMiembroInviteCode(data.code as string);
      setMiembroInviteExpiresAt(data.expires_at as string);
      setCodes((prev) => [
        {
          id: data.id as string,
          code: data.code as string,
          tier: "vip",
          maxUses: Number(data.max_uses ?? 1),
          currentUses: 0,
          expiresAt: new Date(data.expires_at as string),
          link: data.link as string,
          status: "active",
        },
        ...prev,
      ]);
    } catch {
      setMiembroInviteError("Error de conexión");
    } finally {
      setMiembroInviteLoading(false);
    }
  };

  const handleCopyMiembroInvite = async () => {
    if (!miembroInviteUrl) return;
    try {
      await navigator.clipboard.writeText(miembroInviteUrl);
      setMiembroInviteCopied(true);
      setTimeout(() => setMiembroInviteCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const handleCopyIntlInvite = async () => {
    if (!intlInviteUrl) return;
    try {
      await navigator.clipboard.writeText(intlInviteUrl);
      setIntlInviteCopied(true);
      setTimeout(() => setIntlInviteCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Error copying:", err);
    }
  };
  
  const getTierIcon = (t: string) => {
    switch (t) {
      case "ultra_vip": return <Crown className="w-4 h-4" />;
      case "vip": return <Sparkles className="w-4 h-4" />;
      default: return <Gift className="w-4 h-4" />;
    }
  };
  
  const getTierColor = (t: string) => {
    switch (t) {
      case "ultra_vip": return "text-yellow-400 bg-yellow-500/20";
      case "vip": return "text-purple-400 bg-purple-500/20";
      default: return "text-blue-400 bg-blue-500/20";
    }
  };

  const handleGenerateOneTimeLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setOneTimeLinkError(null);
    setOneTimeLinkUrl(null);
    if (!oneTimeLinkPassword.trim()) {
      setOneTimeLinkError("Ingresa tu contraseña de admin");
      return;
    }
    setOneTimeLinkLoading(true);
    try {
      const res = await fetch("/api/admin/one-time-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: oneTimeLinkPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setOneTimeLinkError(data.error || "Error al generar el link");
        setOneTimeLinkPassword("");
        return;
      }
      setOneTimeLinkUrl(data.url ?? null);
      setOneTimeLinkPassword("");
    } catch {
      setOneTimeLinkError("Error de conexión");
    } finally {
      setOneTimeLinkLoading(false);
    }
  };

  const copyOneTimeLink = async () => {
    if (!oneTimeLinkUrl) return;
    try {
      await navigator.clipboard.writeText(oneTimeLinkUrl);
      setOneTimeLinkCopied(true);
      setTimeout(() => setOneTimeLinkCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const handleGenerateInvitation1Min = async (e: React.FormEvent) => {
    e.preventDefault();
    setInvitation1MinError(null);
    setInvitation1MinUrl(null);
    if (!invitation1MinPassword.trim()) {
      setInvitation1MinError("Ingresa tu contraseña de admin");
      return;
    }
    setInvitation1MinLoading(true);
    try {
      const layoutPayload: Record<string, string | number> = {};
      if (invitationLayout.title.trim()) layoutPayload.title = invitationLayout.title.trim();
      if (invitationLayout.subtitle.trim()) layoutPayload.subtitle = invitationLayout.subtitle.trim();
      if (invitationLayout.message.trim()) layoutPayload.message = invitationLayout.message.trim();
      if (invitationLayout.theme) layoutPayload.theme = invitationLayout.theme;
      if (invitationLayout.cta_text.trim()) layoutPayload.cta_text = invitationLayout.cta_text.trim();
      if (invitationLayout.image_url.trim()) layoutPayload.image_url = invitationLayout.image_url.trim();
      if (invitationLayout.background_image_url.trim()) layoutPayload.background_image_url = invitationLayout.background_image_url.trim();
      const spots = Number(invitationLayout.spots_remaining);
      if (!Number.isNaN(spots) && spots >= 0) layoutPayload.spots_remaining = Math.floor(spots);
      const maxSp = Number(invitationLayout.max_spots);
      if (!Number.isNaN(maxSp) && maxSp >= 0) layoutPayload.max_spots = Math.floor(maxSp);

      const body: { password: string; valid_for_seconds?: number; layout?: Record<string, string | number> } = {
        password: invitation1MinPassword,
      };
      if (invitationValiditySeconds !== 1800) body.valid_for_seconds = invitationValiditySeconds;
      if (Object.keys(layoutPayload).length > 0) body.layout = layoutPayload;

      const res = await fetch("/api/invitation-codes/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInvitation1MinError(data.error || "Error al generar el link");
        setInvitation1MinPassword("");
        return;
      }
      setInvitation1MinUrl(data.url ?? null);
      setInvitation1MinExpiresAt(data.valid_until_iso ?? null);
      setInvitation1MinPassword("");
    } catch {
      setInvitation1MinError("Error de conexión");
    } finally {
      setInvitation1MinLoading(false);
    }
  };

  const copyInvitation1MinLink = async () => {
    if (!invitation1MinUrl) return;
    try {
      await navigator.clipboard.writeText(invitation1MinUrl);
      setInvitation1MinCopied(true);
      setTimeout(() => setInvitation1MinCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };
  
  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <AdminPageHeader description="Códigos con tier, descuento y expiración. Config de página invite → Invit. premium." />
      
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-8 space-y-8">

        {/* AR direct VIP invitation — fullscreen video → MP VIP checkout */}
        <div className="bg-amber-500/10 rounded-2xl p-6 mb-8 border border-amber-500/30">
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2 text-amber-400">
            <span>🇦🇷</span>
            <Crown className="h-5 w-5" />
            Invitación VIP Argentina (expira)
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Genera un link directo a <code className="px-1.5 py-0.5 rounded bg-zinc-900 text-amber-300 text-xs">/exclusive-ar</code> que muestra el video con CTA y va directo al
            checkout de <strong>MercadoPago VIP</strong>. Ideal para enviar por DM/WhatsApp a alguien específico:
            si no compra antes del tiempo configurado, el link expira y muestra una pantalla de bloqueo.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Expira en (segundos)</label>
              <input
                type="number"
                min={30}
                max={86400}
                step={10}
                value={arInviteSeconds}
                onChange={(e) => setArInviteSeconds(Math.max(30, parseInt(e.target.value) || 120))}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm tabular-nums"
              />
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <p className="text-[10px] text-zinc-500">30s – 24h</p>
                <button
                  type="button"
                  onClick={() => setArInviteSeconds(60)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-amber-300"
                >
                  60s
                </button>
                <button
                  type="button"
                  onClick={() => setArInviteSeconds(120)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-amber-300"
                >
                  2min
                </button>
                <button
                  type="button"
                  onClick={() => setArInviteSeconds(300)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-amber-300"
                >
                  5min
                </button>
                <button
                  type="button"
                  onClick={() => setArInviteSeconds(900)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-amber-300"
                >
                  15min
                </button>
                <button
                  type="button"
                  onClick={() => setArInviteSeconds(3600)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-amber-300"
                >
                  1h
                </button>
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Usos máximos</label>
              <input
                type="number"
                min={1}
                max={1000}
                value={arInviteMaxUses}
                onChange={(e) => setArInviteMaxUses(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
              />
              <p className="text-[10px] text-zinc-500 mt-1">1 = solo una persona</p>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerateArDirectInvite}
                disabled={arInviteLoading}
                className="w-full sm:w-auto px-6 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 rounded-lg text-black font-medium text-sm flex items-center gap-2"
              >
                {arInviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Generar
              </button>
            </div>
          </div>

          {arInviteError && (
            <p className="text-red-400 text-xs mb-2">{arInviteError}</p>
          )}

          {arInviteUrl && (
            <div className="space-y-3">
              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-amber-400 shrink-0" />
                  <code className="text-xs text-zinc-200 break-all flex-1">{arInviteUrl}</code>
                  <button
                    onClick={handleCopyArInvite}
                    className="shrink-0 px-2 py-1 bg-amber-500 hover:bg-amber-600 rounded text-black text-xs font-medium flex items-center gap-1"
                  >
                    {arInviteCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {arInviteCopied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <div className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span>Código: <code className="text-amber-300">{arInviteCode}</code></span>
                  {arInviteExpiresAt && <InviteExpiryBadge expiresAt={arInviteExpiresAt} />}
                  <span>Usos máx: <span className="text-zinc-300">{arInviteMaxUses}</span></span>
                </div>
              </div>

              <InviteDmTemplate
                link={arInviteUrl}
                expirationSeconds={arInviteSeconds}
                destination="exclusive_ar"
              />
            </div>
          )}
        </div>

        {/* International direct VIP invitation — fullscreen video → Stripe USD */}
        <div className="bg-blue-500/10 rounded-2xl p-6 mb-8 border border-blue-500/30">
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2 text-blue-400">
            <span>🌎</span>
            <Crown className="h-5 w-5" />
            Invitación VIP Internacional (expira)
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Genera un link directo a <code className="px-1.5 py-0.5 rounded bg-zinc-900 text-blue-300 text-xs">/exclusive</code> que muestra el video con CTA y va al
            checkout de <strong>Stripe USD</strong>. Mismo componente Mux que la versión AR, sin distracciones.
            Para mandar por DM a seguidoras de US / MX / ES / etc. Si no compra antes del tiempo configurado, el link expira.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Expira en (segundos)</label>
              <input
                type="number"
                min={30}
                max={86400}
                step={10}
                value={intlInviteSeconds}
                onChange={(e) => setIntlInviteSeconds(Math.max(30, parseInt(e.target.value) || 120))}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm tabular-nums"
              />
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <p className="text-[10px] text-zinc-500">30s – 24h</p>
                {[60, 120, 300, 900, 3600].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setIntlInviteSeconds(s)}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-blue-300"
                  >
                    {s < 60 ? `${s}s` : s < 3600 ? `${s / 60}min` : `${s / 3600}h`}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Usos máximos</label>
              <input
                type="number"
                min={1}
                max={1000}
                value={intlInviteMaxUses}
                onChange={(e) => setIntlInviteMaxUses(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
              />
              <p className="text-[10px] text-zinc-500 mt-1">1 = solo una persona</p>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerateIntlDirectInvite}
                disabled={intlInviteLoading}
                className="w-full sm:w-auto px-6 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded-lg text-white font-medium text-sm flex items-center gap-2"
              >
                {intlInviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Generar
              </button>
            </div>
          </div>

          {intlInviteError && (
            <p className="text-red-400 text-xs mb-2">{intlInviteError}</p>
          )}

          {intlInviteUrl && (
            <div className="space-y-3">
              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-blue-400 shrink-0" />
                  <code className="text-xs text-zinc-200 break-all flex-1">{intlInviteUrl}</code>
                  <button
                    onClick={handleCopyIntlInvite}
                    className="shrink-0 px-2 py-1 bg-blue-500 hover:bg-blue-600 rounded text-white text-xs font-medium flex items-center gap-1"
                  >
                    {intlInviteCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {intlInviteCopied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <div className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span>Code: <code className="text-blue-300">{intlInviteCode}</code></span>
                  {intlInviteExpiresAt && <InviteExpiryBadge expiresAt={intlInviteExpiresAt} />}
                  <span>Max uses: <span className="text-zinc-300">{intlInviteMaxUses}</span></span>
                </div>
              </div>

              <InviteDmTemplate
                link={intlInviteUrl}
                expirationSeconds={intlInviteSeconds}
                destination="exclusive"
              />
            </div>
          )}
        </div>

        {/* /invitacion-miembro — DualCTA VIP video landing (MP + Stripe) */}
        <div className="bg-amber-500/10 rounded-2xl p-6 mb-8 border border-amber-500/30">
          <h2 className="text-xl font-semibold mb-1 flex items-center gap-2 text-amber-400">
            <Sparkles className="h-5 w-5" />
            Invitación Miembro — Video DualCTA
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Genera un link a{" "}
            <code className="px-1.5 py-0.5 rounded bg-zinc-900 text-amber-300 text-xs">
              /invitacion-miembro?code=CODE
            </code>{" "}
            — landing de video fullscreen con "Ser Miembro" (AR → MP, resto → Stripe) y "Canal gratis".
            Funciona para seguidoras de cualquier país.
          </p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Válido por (segundos)</label>
              <select
                value={miembroInviteSeconds}
                onChange={(e) => setMiembroInviteSeconds(Number(e.target.value))}
                className="w-full bg-black/30 rounded-lg px-3 py-2 text-sm text-gray-300 border border-zinc-700 focus:outline-none"
              >
                <option value={60}>1 min</option>
                <option value={120}>2 min</option>
                <option value={300}>5 min</option>
                <option value={600}>10 min</option>
                <option value={1800}>30 min</option>
                <option value={3600}>1 hora</option>
                <option value={86400}>24 horas</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Usos máximos</label>
              <select
                value={miembroInviteMaxUses}
                onChange={(e) => setMiembroInviteMaxUses(Number(e.target.value))}
                className="w-full bg-black/30 rounded-lg px-3 py-2 text-sm text-gray-300 border border-zinc-700 focus:outline-none"
              >
                <option value={1}>1 uso</option>
                <option value={3}>3 usos</option>
                <option value={5}>5 usos</option>
                <option value={10}>10 usos</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleGenerateMiembroInvite}
            disabled={miembroInviteLoading}
            className="w-full sm:w-auto px-6 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 rounded-lg text-black font-semibold text-sm flex items-center gap-2 mb-3"
          >
            {miembroInviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Generar link /invitacion-miembro
          </button>

          {miembroInviteError && (
            <p className="text-red-400 text-xs mb-2">{miembroInviteError}</p>
          )}

          {miembroInviteUrl && (
            <div className="space-y-3">
              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-amber-400 shrink-0" />
                  <code className="text-xs text-zinc-200 break-all flex-1">{miembroInviteUrl}</code>
                  <button
                    onClick={handleCopyMiembroInvite}
                    className="shrink-0 px-2 py-1 bg-amber-500 hover:bg-amber-400 rounded text-black text-xs font-semibold flex items-center gap-1"
                  >
                    {miembroInviteCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {miembroInviteCopied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <div className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span>Code: <code className="text-amber-300">{miembroInviteCode}</code></span>
                  {miembroInviteExpiresAt && <InviteExpiryBadge expiresAt={miembroInviteExpiresAt} />}
                  <span>Max uses: <span className="text-zinc-300">{miembroInviteMaxUses}</span></span>
                </div>
              </div>

              <InviteDmTemplate
                link={miembroInviteUrl}
                expirationSeconds={miembroInviteSeconds}
                destination="invitacion_miembro"
              />
            </div>
          )}
        </div>

        {/* Link admin /ar/invitacion - entrada infinita */}
        <div className="bg-emerald-500/10 rounded-2xl p-6 mb-8 border border-emerald-500/30">
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2 text-emerald-400">
            <InfinityIcon className="h-5 w-5" />
            Link directo /acceso (vista previa, sin código)
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Abre <strong>/acceso</strong> sin código de invitación — muestra <em>BioTemplatePremiumSimple</em> directamente, sin validación ni countdown. Ideal para previsualizar la página de suscripción antes de compartir links reales.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              readOnly
              value={adminInvitacionLink}
              className="flex-1 bg-black/30 rounded-lg px-4 py-3 text-sm text-gray-300 outline-none"
            />
            <button
              type="button"
              onClick={copyAdminLink}
              className="flex-shrink-0 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
            >
              {adminLinkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {adminLinkCopied ? "Copiado" : "Copiar link"}
            </button>
          </div>
        </div>

        {/* Link de un solo uso: compartir y abrir 1 vez */}
        <div className="bg-white/5 rounded-2xl p-6 mb-8 border border-white/10">
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Link de un solo uso
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Genera un único link para compartir. Expira en <strong>5 minutos</strong>. Solo puede abrirlo <strong>una vez</strong> la persona que lo reciba; después el link queda invalidado.
          </p>
          {!oneTimeLinkUrl ? (
            <form onSubmit={handleGenerateOneTimeLink} className="flex flex-col sm:flex-row gap-3">
              <input
                type="password"
                value={oneTimeLinkPassword}
                onChange={(e) => setOneTimeLinkPassword(e.target.value)}
                placeholder="Contraseña de admin"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={oneTimeLinkLoading}
              />
              <button
                type="submit"
                disabled={oneTimeLinkLoading}
                className="px-6 py-3 bg-white text-black font-semibold rounded-lg hover:bg-white/90 disabled:opacity-50"
              >
                {oneTimeLinkLoading ? "Generando…" : "Generar link"}
              </button>
            </form>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 bg-black/30 rounded-lg p-3">
                <input
                  type="text"
                  readOnly
                  value={oneTimeLinkUrl}
                  className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate"
                />
                <button
                  type="button"
                  onClick={copyOneTimeLink}
                  className="flex-shrink-0 p-2 bg-white/10 rounded-lg hover:bg-white/20 flex items-center gap-2"
                >
                  {oneTimeLinkCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {oneTimeLinkCopied ? "Copiado" : "Copiar"}
                </button>
              </div>
              <p className="text-amber-400/90 text-xs">
                Expira en 5 minutos. Solo puede abrirlo una vez la persona que lo reciba. Compártelo por un canal seguro.
              </p>
              <button
                type="button"
                onClick={() => setOneTimeLinkUrl(null)}
                className="text-sm text-white/60 hover:text-white"
              >
                Generar otro link
              </button>
            </div>
          )}
          {oneTimeLinkError && (
            <p className="mt-3 text-sm text-red-400">{oneTimeLinkError}</p>
          )}
        </div>

        {/* Link de invitación (tiempo + layout) */}
        <div className="bg-amber-500/10 rounded-2xl p-6 mb-8 border border-amber-500/30">
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2 text-amber-400">
            <Link2 className="w-5 h-5" />
            Generar link de invitación
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Genera un link con <strong>countdown real</strong>: cuando el tiempo expira, el link muere. La urgencia del timer es el mecanismo de conversión — no hace falta descuento. Ideal para stories (30 min), posts (24 h) o lanzamientos (7 días).
          </p>
          {!invitation1MinUrl ? (
            <form onSubmit={handleGenerateInvitation1Min} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="password"
                  value={invitation1MinPassword}
                  onChange={(e) => setInvitation1MinPassword(e.target.value)}
                  placeholder="Contraseña de admin"
                  className="flex-1 px-4 py-3 bg-black/30 border border-amber-500/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  disabled={invitation1MinLoading}
                />
                <button
                  type="submit"
                  disabled={invitation1MinLoading}
                  className="px-6 py-3 bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded-lg disabled:opacity-50"
                >
                  {invitation1MinLoading ? "Generando…" : "Generar link"}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Tiempo de validez</label>
                  <select
                    value={invitationValiditySeconds}
                    onChange={(e) => setInvitationValiditySeconds(Number(e.target.value))}
                    className="w-full px-4 py-2.5 bg-black/30 border border-amber-500/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={300}>5 minutos — máxima urgencia</option>
                    <option value={600}>10 minutos</option>
                    <option value={1800}>30 minutos ✓ stories</option>
                    <option value={3600}>1 hora</option>
                    <option value={21600}>6 horas</option>
                    <option value={86400}>24 horas — posts/feed</option>
                    <option value={172800}>48 horas</option>
                    <option value={604800}>7 días</option>
                    <option value={2592000}>30 días</option>
                  </select>
                </div>
              </div>
              <div className="border-t border-amber-500/20 pt-4">
                <p className="text-sm text-amber-400/90 mb-3">Layout de la página destino (opcional)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={invitationLayout.title}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Título"
                    className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                  />
                  <input
                    type="text"
                    value={invitationLayout.subtitle}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, subtitle: e.target.value }))}
                    placeholder="Subtítulo"
                    className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                  />
                  <input
                    type="text"
                    value={invitationLayout.message}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, message: e.target.value }))}
                    placeholder="Mensaje / descripción"
                    className="sm:col-span-2 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                  />
                  <select
                    value={invitationLayout.theme}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, theme: e.target.value as typeof p.theme }))}
                    className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm"
                  >
                    <option value="">Sin tema</option>
                    <option value="light">Claro</option>
                    <option value="dark">Oscuro</option>
                    <option value="brand">Marca</option>
                  </select>
                  <input
                    type="text"
                    value={invitationLayout.cta_text}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, cta_text: e.target.value }))}
                    placeholder="Texto del botón (ej. Entrar)"
                    className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                  />
                  <input
                    type="url"
                    value={invitationLayout.image_url}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, image_url: e.target.value }))}
                    placeholder="URL del logo"
                    className="sm:col-span-2 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                  />
                  <input
                    type="url"
                    value={invitationLayout.background_image_url}
                    onChange={(e) => setInvitationLayout((p) => ({ ...p, background_image_url: e.target.value }))}
                    placeholder="URL imagen de fondo (pantalla completa)"
                    className="sm:col-span-2 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                  />
                  <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Lugares disponibles</label>
                      <input
                        type="number"
                        min={0}
                        value={invitationLayout.spots_remaining === "" ? "" : invitationLayout.spots_remaining}
                        onChange={(e) =>
                          setInvitationLayout((p) => ({
                            ...p,
                            spots_remaining: e.target.value === "" ? "" : Number(e.target.value),
                          }))
                        }
                        placeholder="Ej. 5 (tienta al usuario)"
                        className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Máximo (opcional, para «X de Y»)</label>
                      <input
                        type="number"
                        min={0}
                        value={invitationLayout.max_spots === "" ? "" : invitationLayout.max_spots}
                        onChange={(e) =>
                          setInvitationLayout((p) => ({
                            ...p,
                            max_spots: e.target.value === "" ? "" : Number(e.target.value),
                          }))
                        }
                        placeholder="Ej. 10"
                        className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 bg-black/30 rounded-lg p-3">
                <input
                  type="text"
                  readOnly
                  value={invitation1MinUrl ?? ""}
                  className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate"
                />
                <button
                  type="button"
                  onClick={copyInvitation1MinLink}
                  className="flex-shrink-0 px-3 py-1.5 bg-amber-500/20 rounded-lg hover:bg-amber-500/30 flex items-center gap-2 text-sm font-medium"
                >
                  {invitation1MinCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {invitation1MinCopied ? "¡Copiado!" : "Copiar"}
                </button>
              </div>
              {invitation1MinExpiresAt && (
                <p className="text-amber-400/80 text-xs flex items-center gap-1.5">
                  <span>⏰</span>
                  Expira el{" "}
                  <strong>
                    {new Date(invitation1MinExpiresAt).toLocaleString("es-AR", {
                      timeZone: "America/Argentina/Buenos_Aires",
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </strong>
                  {" "}(hora Argentina). Compartilo antes de que venza.
                </p>
              )}
              <button
                type="button"
                onClick={() => { setInvitation1MinUrl(null); setInvitation1MinExpiresAt(null); }}
                className="text-sm text-white/60 hover:text-white self-start"
              >
                ← Generar otro link
              </button>
            </div>
          )}
          {invitation1MinError && (
            <p className="mt-3 text-sm text-red-400">{invitation1MinError}</p>
          )}
        </div>
        
        {/* Generator Form */}
        <div className="bg-white/5 rounded-2xl p-6 mb-8 border border-white/10">
          <h2 className="text-xl font-semibold mb-6">Nueva Invitación</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Tier */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Tier</label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value as typeof tier)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500"
              >
                <option value="standard">Standard</option>
                <option value="vip">VIP</option>
                <option value="ultra_vip">Ultra VIP</option>
              </select>
            </div>
            
            {/* Max Uses */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Límite de usos</label>
              <input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(Number(e.target.value))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500"
              />
            </div>
            
            {/* Expiration */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Expira en (días)</label>
              <input
                type="number"
                min="1"
                value={expirationDays}
                onChange={(e) => setExpirationDays(Number(e.target.value))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500"
              />
            </div>
            
            {/* Prefix */}
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-400 mb-2">Prefijo (opcional)</label>
              <input
                type="text"
                placeholder="ej: VIP, BSAS, PROMO"
                maxLength={4}
                value={prefix}
                onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 uppercase"
              />
            </div>
          </div>
          
          {generateError && (
            <p className="mb-3 text-sm text-red-400">{generateError}</p>
          )}
          <button
            onClick={handleGenerateCode}
            disabled={generateLoading}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {generateLoading ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Guardando en DB...</>
            ) : (
              <><Plus className="w-5 h-5" />Generar y guardar código</>
            )}
          </button>
        </div>
        
        {/* Generated Codes List */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">
              Códigos Guardados
              <span className="ml-2 text-base text-gray-500 font-normal">({codes.length})</span>
            </h2>
            <button
              onClick={loadCodes}
              disabled={codesLoading}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
              title="Recargar"
            >
              <RefreshCw className={`w-4 h-4 ${codesLoading ? "animate-spin" : ""}`} />
            </button>
          </div>

          {codesError && (
            <p className="mb-4 text-sm text-red-400">{codesError}</p>
          )}

          {codesLoading && codes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
              <p className="text-sm">Cargando códigos...</p>
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay códigos aún. Usá el formulario de arriba para crear el primero.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {codes.map((code) => {
                const statusColor =
                  code.status === "active"
                    ? "text-green-400"
                    : code.status === "expired"
                      ? "text-yellow-400"
                      : code.status === "exhausted"
                        ? "text-orange-400"
                        : "text-gray-500";
                const statusLabel =
                  code.status === "active"
                    ? "Activo"
                    : code.status === "expired"
                      ? "Expirado"
                      : code.status === "exhausted"
                        ? "Sin usos"
                        : "Inactivo";
                return (
                  <div
                    key={code.code}
                    className={`bg-white/5 rounded-xl p-4 border transition-opacity ${
                      code.status !== "active" ? "opacity-60 border-white/5" : "border-white/10"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${getTierColor(code.tier)}`}>
                          {getTierIcon(code.tier)}
                        </div>
                        <div className="min-w-0">
                          <button
                            onClick={() => handleCopyCode(code.code)}
                            className="font-mono font-bold text-lg hover:text-purple-400 transition-colors flex items-center gap-2"
                          >
                            {code.code}
                            {copiedCode === code.code ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 opacity-50" />
                            )}
                          </button>
                          <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                            <span>{code.tier.replace("_", " ").toUpperCase()}</span>
                            <span>{code.currentUses ?? 0}/{code.maxUses} usos</span>
                            <span className={statusColor}>{statusLabel}</span>
                          </div>
                        </div>
                      </div>

                      <div className="shrink-0 flex flex-col items-end gap-1">
                        <div className="text-right text-xs text-gray-500">
                          <div>Expira</div>
                          <div className="text-white/70">
                            {code.expiresAt.toLocaleDateString("es-AR")}
                          </div>
                        </div>
                        {code.id && code.status === "active" && (
                          <button
                            onClick={() => handleDeactivate(code.id!)}
                            className="text-red-400/60 hover:text-red-400 transition-colors p-1"
                            title="Desactivar código"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 bg-black/30 rounded-lg p-3">
                      <LinkIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <input
                        type="text"
                        readOnly
                        value={code.link}
                        className="flex-1 bg-transparent text-sm text-gray-400 outline-none truncate"
                      />
                      <button
                        onClick={() => handleCopyLink(code)}
                        className="flex-shrink-0 p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                      >
                        {copiedCode === code.code ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Instructions */}
        <div className="mt-8 p-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-2xl border border-amber-500/20">
          <h3 className="font-semibold mb-3 text-amber-300">Cómo compartir una invitación</h3>
          <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
            <li>En <strong className="text-white">Generar link de invitación</strong> elegí el tiempo de validez (1 h para stories, 7 días para posts, etc.)</li>
            <li>Ingresá tu contraseña de admin y hacé clic en <em>Generar link</em></li>
            <li>Copiá el link y compartilo por Instagram, WhatsApp, Telegram, etc.</li>
            <li>El seguidor hace clic, ve la página de invitación y puede suscribirse</li>
            <li>El link expira automáticamente — después nadie más puede usarlo</li>
          </ol>
          <div className="mt-4 p-3 bg-black/20 rounded-lg text-xs text-gray-500">
            <strong className="text-gray-400">Tip:</strong> Stories → <strong>30 min</strong>. Posts del feed → <strong>24 h</strong>. Telegram/Email → <strong>7 días</strong>. El countdown visible en la página es el mecanismo de conversión — no uses descuentos (el precio de MP es fijo desde las env vars).
          </div>
        </div>
      </div>
    </div>
  );
}
